import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { Box, Divider, Grid } from "@mui/material";

import {
  ButtonWrapper,
  CloseButton,
  CloseButtonWrapper,
  Content,
  Header,
  ModalWrapper,
} from "../../components/style/modalStyle";

import { CrossIcon } from "../../assets/Icon/ModalIcon";
import UploadFileFieldNew from "./UploadFileFieldNew";
import { UploadIcon } from "../../assets/Icon/dashboredIcon";

import FormInputField from "./FormInputField";
import CustomButton from "./CustomButton";
import SelectInputField from "./SelectInputField";
import CustomButtonFormStep from "./CustomButtonStepperForm";

interface MemberAttachmentModalProps {
  open: boolean;
  handleClose: () => void;
  formik: any;
  filePreview?: any;
  setFilePreview?: any;
  handleFileChange?: (file: File | null) => void;
  closeModal?: () => void;
  onAddMember?: () => void | Promise<void>;
  isEditing?: boolean;
  countryList?: any[];
  stateList?: any[];
}

const MemberAttachmentModal = ({
  open,
  handleClose,
  formik,
  filePreview,
  setFilePreview,
  handleFileChange,
  closeModal,
  onAddMember,
  isEditing,
  countryList = [],
  stateList = [],
}: MemberAttachmentModalProps) => {
  return (
    <Modal open={open} onClose={handleClose}>
      <ModalWrapper
        sx={{
          width: {
            xs: "95%",
            sm: "850px",
          },
          maxHeight: "90vh",
        }}
      >
        {/* Close Button */}
        <CloseButtonWrapper>
          <CloseButton onClick={handleClose}>
            <CrossIcon />
          </CloseButton>
        </CloseButtonWrapper>

        {/* Header */}
        <Header>
          <Typography
            sx={{
              fontSize: "30px",
              fontWeight: 700,
            }}
          >
            {isEditing ? "Edit Member Details" : "Add Member Details"}
          </Typography>
        </Header>

        <Divider />

        {/* Content */}
        <Content
          sx={{
            overflowY: "auto",
            maxHeight: "65vh",
            padding: "20px",
          }}
        >
          <Grid container spacing={3}>
            {/* First Name */}
            <Grid item xs={12} md={4}>
              <FormInputField
                placeholder="Enter first name"
                formik={formik}
                name="member_first_name"
                label="First Name"
                showBorder={true}
              />
            </Grid>

            {/* Middle Name */}
            <Grid item xs={12} md={4}>
              <FormInputField
                placeholder="Enter middle name"
                formik={formik}
                name="member_middle_name"
                label="Middle Name"
                showBorder={true}
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} md={4}>
              <FormInputField
                placeholder="Enter last name"
                formik={formik}
                name="member_last_name"
                label="Last Name"
                showBorder={true}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <FormInputField
                placeholder="Enter phone number"
                formik={formik}
                name="member_phone_number"
                label="Phone Number"
                showBorder={true}
                isPhnNo={true}
                phoneName="member_phone_number"
                countryCodeName="member_country_code"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <FormInputField
                placeholder="Enter email address"
                formik={formik}
                name="member_email"
                label="Email Address"
                showBorder={true}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <FormInputField
                placeholder="Enter address"
                formik={formik}
                name="member_address"
                label="Address"
                showBorder={true}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={6}>
              <FormInputField
                placeholder="Enter city"
                formik={formik}
                name="member_city"
                label="City"
                showBorder={true}
              />
            </Grid>

            {/* Zip */}
            <Grid item xs={12} md={6}>
              <FormInputField
                placeholder="Enter zip code"
                formik={formik}
                name="member_zip_code"
                label="Zip Code"
                showBorder={true}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <SelectInputField
                formik={formik}
                placeholder="Select Country"
                label="Country"
                menuData={countryList}
                name="member_country"
                showBorder={true}
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} md={6}>
              {formik.values.member_country === 233 ? (
                <SelectInputField
                  formik={formik}
                  placeholder="Select State"
                  label="State"
                  menuData={stateList}
                  name="member_state"
                  isState={true}
                  sendValue={true}
                  showBorder={true}
                />
              ) : (
                <FormInputField
                  placeholder="Enter state"
                  formik={formik}
                  name="member_state"
                  label="State"
                  showBorder={true}
                />
              )}
            </Grid>

            {/* Passport Upload */}
            <Grid item xs={12}>
              <UploadFileFieldNew
                title={"Passport"}
                wrapperPadding="0px"
                icon={<UploadIcon />}
                name="member_attachment"
                formik={formik}
                handleFileChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0] || null;

                  handleFileChange?.(file);

                  if (file && setFilePreview) {
                    const previewUrl = URL.createObjectURL(file);
                    setFilePreview(previewUrl);
                  }
                }}
                filePreview={filePreview}
                {...(setFilePreview && { setFilePreview })}
                showInstruction={true}
              />
            </Grid>
          </Grid>

          {/* Footer */}
          <ButtonWrapper
            sx={{
              display: "flex",
              marginTop: "20px",
              justifyContent: "flex-end",
              gap: "12px",
              px: 0,
              py: 2,
            }}
          >
            <CustomButtonFormStep
              btnTitle="Back"
              handleClick={closeModal}
              bgColor="#EAF1FA"
              textColor="#2977CC"
            />

            <CustomButton
              btnTitle={isEditing ? "Update" : "Save"}
              minwidth="191px"
              type="button"
              handleClick={onAddMember}
            />
          </ButtonWrapper>
        </Content>
      </ModalWrapper>
    </Modal>
  );
};

export default MemberAttachmentModal;
